<script setup>
import { onBeforeMount, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  useOvvaBackend,
  getOvvaPat,
  setOvvaPat,
} from 'dashboard/composables/useOvvaBackend';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ChannelConnections from './ChannelConnections.vue';

const { t } = useI18n();
const { accountId } = useAccount();
const { callBackend } = useOvvaBackend();

// useOvvaBackend() кладёт код ответа в error.status — по нему отличаем
// «доступ отклонён, PAT истёк/невалиден/не введён» (401 — сам embed-токен
// панели; 403 — SuperAdminPatGuard отклонил личный Access Token) от обычной
// ошибки валидации/сети.
const isAuthRejected = error => error?.status === 401 || error?.status === 403;

const showAuthGate = ref(!getOvvaPat());
const patInputValue = ref('');
const patError = ref('');

const isFetching = ref(false);
const isSaving = ref(false);
const isTesting = ref(false);

const client = ref(null);

const toneOptions = [
  { value: 'neutral', label: t('AI_SETTINGS_MGMT.BEHAVIOR.TONE.NEUTRAL') },
  { value: 'friendly', label: t('AI_SETTINGS_MGMT.BEHAVIOR.TONE.FRIENDLY') },
  { value: 'formal', label: t('AI_SETTINGS_MGMT.BEHAVIOR.TONE.FORMAL') },
];

const form = reactive({
  tone: 'neutral',
  aiModel: '',
  aiBaseUrl: '',
  aiApiKey: '',
  logoUrl: '',
  customSystemPrompt: '',
  minConfidenceThreshold: 0.35,
  maxClarifications: 2,
  useInternet: false,
  requireManualReview: false,
  workStart: '',
  workEnd: '',
  slaUnassignedMinutes: 5,
  slaNoReplyMinutes: 15,
});

const applyClientToForm = c => {
  form.tone = c.tone || 'neutral';
  form.aiModel = c.aiModel || '';
  form.aiBaseUrl = c.aiBaseUrl || '';
  form.aiApiKey = '';
  form.logoUrl = c.logoUrl || '';
  form.customSystemPrompt = c.customSystemPrompt || '';
  form.minConfidenceThreshold = c.minConfidenceThreshold ?? 0.35;
  form.maxClarifications = c.maxClarifications ?? 2;
  form.useInternet = !!c.useInternet;
  form.requireManualReview = !!c.requireManualReview;
  form.workStart = c.workStart || '';
  form.workEnd = c.workEnd || '';
  form.slaUnassignedMinutes = c.slaUnassignedMinutes ?? 5;
  form.slaNoReplyMinutes = c.slaNoReplyMinutes ?? 15;
};

// Возврат к экрану ввода токена — не стираем то, что уже лежит в
// localStorage (пользователь мог просто временно потерять сеть), только
// переключаем экран и показываем причину, чтобы не зависать в сломанном
// состоянии, если PAT истёк или стал невалидным.
const resetToGate = message => {
  client.value = null;
  showAuthGate.value = true;
  patError.value = message || '';
};

const fetchClient = async () => {
  isFetching.value = true;
  try {
    const list = await callBackend('/clients');
    const arr = Array.isArray(list) ? list : [];
    const found = arr.find(c => c.id === accountId.value) || arr[0] || null;
    client.value = found;
    if (found) applyClientToForm(found);
  } catch (error) {
    if (isAuthRejected(error)) {
      resetToGate(error.message);
    } else {
      useAlert(error.message || t('AI_SETTINGS_MGMT.API.FETCH_ERROR'));
    }
  } finally {
    isFetching.value = false;
  }
};

const submitPat = () => {
  const value = patInputValue.value.trim();
  if (value.length < 10) {
    patError.value = t('AI_SETTINGS_MGMT.PAT.TOO_SHORT');
    return;
  }
  setOvvaPat(value);
  patInputValue.value = '';
  patError.value = '';
  showAuthGate.value = false;
  fetchClient();
};

const disconnectPat = () => {
  setOvvaPat('');
  client.value = null;
  showAuthGate.value = true;
  patError.value = '';
};

const saveSettings = async () => {
  if (!client.value) return;
  isSaving.value = true;
  const body = {
    tone: form.tone,
    aiModel: form.aiModel.trim() || null,
    aiBaseUrl: form.aiBaseUrl.trim() || null,
    logoUrl: form.logoUrl.trim() || null,
    customSystemPrompt: form.customSystemPrompt || null,
    minConfidenceThreshold: Number(form.minConfidenceThreshold) || 0.35,
    maxClarifications: Number(form.maxClarifications) || 0,
    useInternet: form.useInternet,
    requireManualReview: form.requireManualReview,
    workStart: form.workStart.trim() || null,
    workEnd: form.workEnd.trim() || null,
    slaUnassignedMinutes: Number(form.slaUnassignedMinutes) || 5,
    slaNoReplyMinutes: Number(form.slaNoReplyMinutes) || 15,
  };
  const key = form.aiApiKey.trim();
  if (key) body.aiApiKey = key;

  try {
    const updated = await callBackend(`/clients/${client.value.id}`, {
      method: 'PATCH',
      body,
    });
    client.value = updated;
    form.aiApiKey = '';
    useAlert(t('AI_SETTINGS_MGMT.API.SAVE_SUCCESS'));
  } catch (error) {
    if (isAuthRejected(error)) {
      resetToGate(error.message);
    } else {
      useAlert(error.message || t('AI_SETTINGS_MGMT.API.SAVE_ERROR'));
    }
  } finally {
    isSaving.value = false;
  }
};

const testConnection = async () => {
  if (!client.value) return;
  isTesting.value = true;
  try {
    const r = await callBackend(
      `/clients/${client.value.id}/test-ai-connection`,
      { method: 'POST' }
    );
    if (r && r.status === 'ok') {
      useAlert(t('AI_SETTINGS_MGMT.TEST.SUCCESS'));
    } else {
      useAlert(r?.message || r?.error || t('AI_SETTINGS_MGMT.TEST.ERROR'));
    }
  } catch (error) {
    if (isAuthRejected(error)) {
      resetToGate(error.message);
    } else {
      useAlert(error.message || t('AI_SETTINGS_MGMT.TEST.ERROR'));
    }
  } finally {
    isTesting.value = false;
  }
};

onBeforeMount(() => {
  if (!showAuthGate.value) fetchClient();
});
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('AI_SETTINGS_MGMT.HEADER')"
        :description="$t('AI_SETTINGS_MGMT.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col w-full max-w-3xl gap-0">
        <ChannelConnections />

        <SectionLayout
          v-if="showAuthGate"
          with-border
          :title="$t('AI_SETTINGS_MGMT.PAT.TITLE')"
          :description="$t('AI_SETTINGS_MGMT.PAT.DESCRIPTION')"
        >
          <p class="max-w-md mb-3 text-body-main text-n-slate-11">
            {{ $t('AI_SETTINGS_MGMT.PAT.HINT') }}
          </p>
          <form
            class="flex flex-col max-w-md gap-3"
            @submit.prevent="submitPat"
          >
            <Input
              v-model="patInputValue"
              autocomplete="new-password"
              type="password"
              :label="$t('AI_SETTINGS_MGMT.PAT.TOKEN_LABEL')"
              :placeholder="$t('AI_SETTINGS_MGMT.PAT.TOKEN_PLACEHOLDER')"
              :message="patError"
              :message-type="patError ? 'error' : 'info'"
            />
            <div>
              <Button
                type="submit"
                size="sm"
                :label="$t('AI_SETTINGS_MGMT.PAT.CONNECT')"
              />
            </div>
          </form>
        </SectionLayout>

        <template v-else>
          <woot-loading-state
            v-if="isFetching"
            :message="$t('AI_SETTINGS_MGMT.LOADING')"
          />
          <p v-else-if="!client" class="py-10 text-body-main text-n-slate-11">
            {{ $t('AI_SETTINGS_MGMT.NO_CLIENT') }}
          </p>
          <form v-else @submit.prevent="saveSettings">
            <SectionLayout
              with-border
              :title="$t('AI_SETTINGS_MGMT.BEHAVIOR.TITLE')"
              :description="$t('AI_SETTINGS_MGMT.BEHAVIOR.DESCRIPTION')"
            >
              <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-1">
                  <span class="text-heading-3 text-n-slate-12">
                    {{ $t('AI_SETTINGS_MGMT.BEHAVIOR.TONE.LABEL') }}
                  </span>
                  <Select v-model="form.tone" :options="toneOptions" />
                </div>
                <TextArea
                  v-model="form.customSystemPrompt"
                  :label="$t('AI_SETTINGS_MGMT.BEHAVIOR.PROMPT.LABEL')"
                  :placeholder="
                    $t('AI_SETTINGS_MGMT.BEHAVIOR.PROMPT.PLACEHOLDER')
                  "
                />
                <div class="grid grid-cols-2 gap-4">
                  <Input
                    v-model="form.minConfidenceThreshold"
                    autocomplete="off"
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    :label="$t('AI_SETTINGS_MGMT.BEHAVIOR.CONFIDENCE.LABEL')"
                  />
                  <Input
                    v-model="form.maxClarifications"
                    autocomplete="off"
                    type="number"
                    min="0"
                    max="5"
                    :label="
                      $t('AI_SETTINGS_MGMT.BEHAVIOR.CLARIFICATIONS.LABEL')
                    "
                  />
                </div>
                <label class="flex items-center gap-2 cursor-pointer w-fit">
                  <Switch v-model="form.useInternet" />
                  <span class="text-body-main text-n-slate-12">
                    {{ $t('AI_SETTINGS_MGMT.BEHAVIOR.USE_INTERNET.LABEL') }}
                  </span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer w-fit">
                  <Switch v-model="form.requireManualReview" />
                  <span class="text-body-main text-n-slate-12">
                    {{ $t('AI_SETTINGS_MGMT.BEHAVIOR.MANUAL_REVIEW.LABEL') }}
                  </span>
                </label>
              </div>
            </SectionLayout>

            <SectionLayout
              with-border
              :title="$t('AI_SETTINGS_MGMT.MODEL.TITLE')"
              :description="$t('AI_SETTINGS_MGMT.MODEL.DESCRIPTION')"
            >
              <div class="flex flex-col gap-4">
                <Input
                  v-model="form.aiModel"
                  autocomplete="off"
                  :label="$t('AI_SETTINGS_MGMT.MODEL.AI_MODEL.LABEL')"
                  :placeholder="
                    $t('AI_SETTINGS_MGMT.MODEL.AI_MODEL.PLACEHOLDER')
                  "
                />
                <Input
                  v-model="form.aiBaseUrl"
                  autocomplete="off"
                  :label="$t('AI_SETTINGS_MGMT.MODEL.BASE_URL.LABEL')"
                  :placeholder="
                    $t('AI_SETTINGS_MGMT.MODEL.BASE_URL.PLACEHOLDER')
                  "
                />
                <Input
                  v-model="form.aiApiKey"
                  autocomplete="new-password"
                  type="password"
                  :label="$t('AI_SETTINGS_MGMT.MODEL.API_KEY.LABEL')"
                  :placeholder="
                    $t('AI_SETTINGS_MGMT.MODEL.API_KEY.PLACEHOLDER')
                  "
                />
              </div>
            </SectionLayout>

            <SectionLayout
              with-border
              :title="$t('AI_SETTINGS_MGMT.BRANDING.TITLE')"
              :description="$t('AI_SETTINGS_MGMT.BRANDING.DESCRIPTION')"
            >
              <div class="flex flex-col gap-1">
                <Input
                  v-model="form.logoUrl"
                  autocomplete="off"
                  :label="$t('AI_SETTINGS_MGMT.BRANDING.LOGO.LABEL')"
                  :placeholder="
                    $t('AI_SETTINGS_MGMT.BRANDING.LOGO.PLACEHOLDER')
                  "
                />
                <p class="mt-1 mb-0 text-label-small text-n-slate-11">
                  {{ $t('AI_SETTINGS_MGMT.BRANDING.LOGO.HINT') }}
                </p>
              </div>
            </SectionLayout>

            <SectionLayout
              with-border
              :title="$t('AI_SETTINGS_MGMT.HOURS.TITLE')"
              :description="$t('AI_SETTINGS_MGMT.HOURS.DESCRIPTION')"
            >
              <div class="flex flex-col gap-4">
                <div class="grid grid-cols-2 gap-4">
                  <Input
                    v-model="form.workStart"
                    autocomplete="off"
                    :label="$t('AI_SETTINGS_MGMT.HOURS.WORK_START.LABEL')"
                    :placeholder="
                      $t('AI_SETTINGS_MGMT.HOURS.WORK_START.PLACEHOLDER')
                    "
                  />
                  <Input
                    v-model="form.workEnd"
                    autocomplete="off"
                    :label="$t('AI_SETTINGS_MGMT.HOURS.WORK_END.LABEL')"
                    :placeholder="
                      $t('AI_SETTINGS_MGMT.HOURS.WORK_END.PLACEHOLDER')
                    "
                  />
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <Input
                    v-model="form.slaUnassignedMinutes"
                    autocomplete="off"
                    type="number"
                    min="1"
                    :label="$t('AI_SETTINGS_MGMT.HOURS.SLA_UNASSIGNED.LABEL')"
                  />
                  <Input
                    v-model="form.slaNoReplyMinutes"
                    autocomplete="off"
                    type="number"
                    min="1"
                    :label="$t('AI_SETTINGS_MGMT.HOURS.SLA_NO_REPLY.LABEL')"
                  />
                </div>
              </div>
            </SectionLayout>

            <div class="flex items-center gap-3 pt-6 pb-8">
              <Button
                type="submit"
                size="sm"
                :label="$t('AI_SETTINGS_MGMT.SAVE')"
                :is-loading="isSaving"
              />
              <Button
                variant="faded"
                color="slate"
                size="sm"
                icon="i-lucide-flask-conical"
                :label="$t('AI_SETTINGS_MGMT.TEST.BUTTON')"
                :is-loading="isTesting"
                @click.prevent="testConnection"
              />
              <span
                class="text-label-small text-n-slate-11 ltr:ml-auto rtl:mr-auto"
              >
                {{ $t('AI_SETTINGS_MGMT.PAT.CONNECTED') }}
                <button
                  type="button"
                  class="text-n-blue-11 hover:underline"
                  @click="disconnectPat"
                >
                  {{ $t('AI_SETTINGS_MGMT.PAT.DISCONNECT') }}
                </button>
              </span>
            </div>
          </form>
        </template>
      </div>
    </template>
  </SettingsLayout>
</template>
